import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { CustomThemeProvider } from './contexts/ThemeContext';
import { MegaMenuProvider } from './contexts/MegaMenuContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppContent } from './AppContent';
import { SidePanelProvider } from './contexts/SidePanelContext';
import { MetadataProvider } from './contexts/MetadataContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ServiceProvider } from './contexts/ServiceContext';
import GlobalLogoutListener from './components/common/GlobalLogoutListener';
import InactivityTimer from './components/common/InactivityTimer';
import { BrandProvider } from './contexts/BrandingContext';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { ModalProvider } from './contexts/ModalContext';
import { ResoProvider } from './contexts/ResoContext';
import { LoaderProvider } from './contexts/LoaderContext';
import AuthCoordinator from './contexts/AuthCoordinator';
import { DebuggerProvider } from './contexts/DebuggerContext';
import { ScrollProvider } from './contexts/ScrollProvider';
import { suppressUnwantedLogs } from './suppressLogs';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { DefaultsProvider } from './contexts/DefaultsContext';
import { HeaderProvider } from './contexts/HeaderContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { PreviewServiceProvider } from './contexts/PreviewServiceContext';
import { PromoteServiceProvider } from './contexts/PromoteServiceContext';
import { useTenantPath } from './hooks/useTenantPath';
import OtpRequestPage from './pages/preview/OtpRequestPage';
import PreviewListingGate from './pages/preview/PreviewListingGate';

suppressUnwantedLogs();

const queryClient = new QueryClient();

const AuthenticatedApp: React.FC = () => {
  const { logout } = useAuth();
  return (
    <>
      <InactivityTimer />
      <WebSocketProvider>
        <ServiceProvider>
          <PromoteServiceProvider>
            <LoaderProvider>
              <CustomThemeProvider>
                <HeaderProvider>
                  <ModalProvider>
                    <SidePanelProvider>
                      <MegaMenuProvider>
                        <DefaultsProvider>
                          <AppContent />
                        </DefaultsProvider>
                      </MegaMenuProvider>
                    </SidePanelProvider>
                  </ModalProvider>
                </HeaderProvider>
              </CustomThemeProvider>
            </LoaderProvider>
          </PromoteServiceProvider>
        </ServiceProvider>
      </WebSocketProvider>

      <GlobalLogoutListener onCustomLogout={logout} />
    </>
  );
};

/**
 * Checks if the current URL is a preview route.
 * Preview routes bypass the full auth flow.
 */
function isPreviewPath(): boolean {
  const { pathname } = window.location;
  // Match /preview/... or /<tenant>/preview/...
  const segments = pathname.split('/').filter(Boolean);
  return segments.includes('preview');
}

/**
 * Preview routes — rendered when URL contains /preview/.
 * OTP and token-based access are unauthenticated.
 * Direct access (no token) requires authentication.
 */
const PreviewRoutes: React.FC = () => {
  const { getTenantRoute } = useTenantPath();

  return (
    <Routes>
      {getTenantRoute('/preview/otp', <OtpRequestPage />)}
      {getTenantRoute('/preview/:listingKey', <PreviewListingGate />)}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

/**
 * PreviewGate — forks between the preview app and the standard
 * authenticated app flow.
 */
const PreviewGate: React.FC = () => {
  if (isPreviewPath()) {
    return (
      <CustomThemeProvider>
        <PreviewServiceProvider>
          <Router>
            <PreviewRoutes />
          </Router>
        </PreviewServiceProvider>
      </CustomThemeProvider>
    );
  }

  return (
    <AuthProvider>
      <ResoProvider>
        <MetadataProvider>
          <AuthCoordinator onStateChange={() => {}}>
            <AuthenticatedApp />
          </AuthCoordinator>
        </MetadataProvider>
      </ResoProvider>
    </AuthProvider>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ScrollProvider>
        <DebuggerProvider>
          <TenantProvider>
            <BrandProvider>
              <FeatureFlagsProvider>
                <AnalyticsProvider>
                  <PreviewGate />
                </AnalyticsProvider>
              </FeatureFlagsProvider>
            </BrandProvider>
          </TenantProvider>
        </DebuggerProvider>
      </ScrollProvider>
    </QueryClientProvider>
  );
}

export default App;
